/**
 * Cliente del Historial (OpenSpec 008) — main API `/history/:module/:entityId`.
 * Timeline de eventos+notas con keyset (lazy load), búsqueda (texto/persona) y
 * pills de módulo en la vista transversal de Contactos.
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

export type HistoryModule =
  | "contact"
  | "quote"
  | "order"
  | "conversation"
  | "seller"
  | "store"
  | "product"
  // El resto del Catálogo (xcons-docs#436). Globales como el producto —se leen con acceso al
  // Catálogo, no a un store— y, por la misma razón, sin notas.
  | "attribute"
  | "attribute_set"
  | "category"
  | "catalog_admin"
  | "import"
  // El producto de un seller en Productos asociados (xcons-docs#611): sus vínculos con el
  // catálogo. Se lee por el seller y, como el Catálogo, sin notas.
  | "linked_product";

export type HistoryActorType = "contact" | "operator" | "automation" | "system" | "external" | "guest";

export interface HistoryEventPayload {
  eventId: string;
  module: HistoryModule;
  entityId: string;
  actorType: HistoryActorType;
  /** userId del generador (NO un contactId). */
  actorId: number | null;
  actorName: string;
  /** Store del evento — el backend lo usa para resolver `actorContactId`. */
  storeCode?: string | null;
  /**
   * Ficha de Contacto del generador, resuelta por el backend desde `(actorId, storeCode)`. `null`
   * cuando el actor no es una persona navegable (invitado, sistema, integraciones) o cuando no
   * tiene ficha en ese store.
   */
  actorContactId?: number | null;
  /**
   * Contacto del registro. En un mensaje de conversación es con quién se habla, o sea el `target`
   * de la frase — lo que permite linkearlo desde el historial de otro contacto (p. ej. el del
   * operador que envió).
   */
  contactId?: number | null;
  /**
   * Solo eventos de Pedido: el `increment_id` (el "#2000005861" que reconoce el cliente).
   * `entityId` es el id interno — es la clave del evento y la que usa la ruta /orders/:id —, así
   * que el número visible viaja aparte. Lo resuelve el backend al leer el timeline.
   */
  orderIncrementId?: string | null;
  /**
   * Solo eventos de Producto: el SKU, que es la clave del catálogo y lo que pide la ruta
   * `/catalog/:sku`. `entityId` puede ser el `entity_id` de Magento —los eventos posteriores a
   * publicar— o el SKU —los del borrador, que todavía no existe en Magento—, así que el SKU
   * para mostrar y linkear viaja aparte. Lo resuelve el backend al leer el timeline.
   */
  productSku?: string | null;
  verb: string;
  conn1: string | null;
  subject: string | null;
  conn2: string | null;
  target: string | null;
  detail: string | null;
  metadata: Record<string, unknown> | null;
  /** Notas por-log adjuntas a este evento (008 Fase 4). */
  notes?: HistoryEventNote[];
}

export interface HistoryNotePayload {
  noteId: string;
  module: HistoryModule;
  entityId: string;
  authorUserId: number;
  authorName: string;
  /** Ficha de Contacto del autor, resuelta por el backend desde `(authorUserId, storeCode)`. */
  authorContactId?: number | null;
  content: string;
  mentions: { userId: number; name: string }[];
  pinned: boolean;
  /** La nota se le muestra al cliente en el front del presupuesto (Q5). */
  visibleOnFront?: boolean;
  scheduledFor: string | null;
  /**
   * Opcional a propósito: los endpoints `/pinned` y `/scheduled` devuelven la fila completa y lo
   * traen, pero el payload que arma el timeline (`json_build_object` en history-query) no lo
   * incluye — ahí la fecha viaja como `occurredAt` de la fila.
   */
  createdAt?: string;
}

/** Nota por-log anidada bajo un evento (008 Fase 4). `parentNoteId` = respuesta en hilo. */
export interface HistoryEventNote {
  noteId: string;
  authorUserId: number;
  authorName: string;
  content: string;
  mentions: { userId: number; name: string }[];
  parentNoteId: string | null;
  createdAt: string;
}

export interface HistoryRow {
  kind: "event" | "note";
  id: string;
  occurredAt: string;
  payload: HistoryEventPayload & Partial<HistoryNotePayload>;
}

/** Labels EN de los verbos (taxonomía cerrada de la planilla — espejo del backend). */
/** Marcadores de mención `@[Nombre](id)` → `@Nombre` para render (OpenSpec 008 Fase 4). */
export function stripMentionMarkers(content: string): string {
  return content.replace(/@\[([^\]]+)\]\((\d+)\)/g, "@$1");
}

export const historyApi = {
  timeline: (
    module: HistoryModule,
    entityId: string | number,
    opts: { before?: string; q?: string; filter?: string; limit?: number } = {},
  ) =>
    axiosInstance
      .get<HistoryRow[]>(`${API_BASE_URL}/history/${module}/${entityId}`, {
        params: {
          ...(opts.before ? { before: opts.before } : {}),
          ...(opts.q ? { q: opts.q } : {}),
          ...(opts.filter ? { filter: opts.filter } : {}),
          ...(opts.limit ? { limit: opts.limit } : {}),
        },
      })
      .then((r) => r.data),
};

/** Notas del Historial (OpenSpec 008 Fase 4). */
export const historyNotesApi = {
  /** Crear nota general (o por-log con `eventId`; programada con `scheduledFor`, solo general). */
  create: (
    module: HistoryModule,
    entityId: string | number,
    body: { content: string; eventId?: string; scheduledFor?: string; visibleOnFront?: boolean },
  ) =>
    axiosInstance
      .post<HistoryNotePayload>(`${API_BASE_URL}/history/${module}/${entityId}/notes`, body)
      .then((r) => r.data),

  /** Responder en el hilo de una nota por-log. */
  reply: (parentId: string, content: string) =>
    axiosInstance
      .post<HistoryNotePayload>(`${API_BASE_URL}/history/notes/${parentId}/replies`, { content })
      .then((r) => r.data),

  /** Editar / fijar / reprogramar (solo autor). */
  /**
   * Editar / fijar / reprogramar / publicar (solo autor).
   *
   * `visibleOnFront` publica o despublica la nota para el cliente. El backend sólo acepta publicar
   * notas generales de un presupuesto; despublicar es siempre libre.
   */
  patch: (
    noteId: string,
    body: {
      content?: string;
      pinned?: boolean;
      scheduledFor?: string;
      visibleOnFront?: boolean;
    },
  ) =>
    axiosInstance
      .patch<HistoryNotePayload>(`${API_BASE_URL}/history/notes/${noteId}`, body)
      .then((r) => r.data),

  remove: (noteId: string) =>
    axiosInstance.delete(`${API_BASE_URL}/history/notes/${noteId}`).then((r) => r.data),

  /**
   * Notas FIJADAS del registro. Tampoco vienen en el timeline (el backend las excluye del stream
   * con `pinned = false`): van primeras en el log, en su propia sección.
   */
  pinned: (module: HistoryModule, entityId: string | number) =>
    axiosInstance
      .get<HistoryNotePayload[]>(`${API_BASE_URL}/history/${module}/${entityId}/pinned`)
      .then((r) => r.data),

  /**
   * Notas programadas PENDIENTES del registro. No vienen en el timeline (el backend las excluye
   * hasta que vencen): van en su propia sección arriba, debajo del compose.
   */
  scheduled: (module: HistoryModule, entityId: string | number) =>
    axiosInstance
      .get<HistoryNotePayload[]>(`${API_BASE_URL}/history/${module}/${entityId}/scheduled`)
      .then((r) => r.data),

  /** Candidatos al autocomplete @: operadores con acceso al registro. */
  mentionables: (module: HistoryModule, entityId: string | number) =>
    axiosInstance
      .get<{ userId: number; name: string }[]>(
        `${API_BASE_URL}/history/${module}/${entityId}/mentionables`,
      )
      .then((r) => r.data),

  /**
   * La agenda del operador: sus próximas actividades de todos los módulos, **incluidas las
   * vencidas**, ordenadas por fecha.
   *
   * Es lo contrario de `nextActivity`: no hay que saber de antemano qué entidades mirar.
   */
  agenda: (limit = 20, stores: string[] = []) =>
    axiosInstance
      .get<
        {
          noteId: string;
          module: HistoryModule;
          entityId: string;
          contactId: number | null;
          scheduledFor: string;
          content: string;
          authorName: string;
        }[]
      >(`${API_BASE_URL}/history/notes/agenda`, {
        // `stores` acota la agenda al store elegido en el header (xcons-docs#296), con la misma
        // forma que `/dashboard/metrics?stores=`. Vacío es todo lo visible, como antes.
        params: { limit, ...(stores.length ? { stores: stores.join(",") } : {}) },
      })
      .then((r) => r.data),

  /** Batch para la columna Next activity: la programada futura más próxima por registro. */
  nextActivity: (entities: { module: HistoryModule; entityId: string }[]) =>
    axiosInstance
      .post<{ module: HistoryModule; entityId: string; scheduledFor: string }[]>(
        `${API_BASE_URL}/history/next-activity`,
        { entities },
      )
      .then((r) => r.data),
};
